import { finishedGoodSyncTarget, sageErrorMessageFor } from '../../../../shared/services/sage-integration.model';
import { SageIntegrationService } from '../../../../shared/services/sage-integration.service';
import { ErrorGroup } from '../dashboard/errors-column.component';

// SageItemPayloadHelpers: Sage's limits for a good sent as a bundle, and the Items columns' own.
const BUNDLE_CODE_MAX = 15;
const BUNDLE_NAME_MAX = 40;
const CODE_MAX = 50;
const NAME_MAX = 255;

/** One item whose code (or name) the panel can change. */
export interface CodeRow {
  itemId: number;
  name: string;
  code: string;
  /** The field's value: the code, or the name for "name too long". */
  draft: string;
  limit: number;
  saving: boolean;
  saved: boolean;
  error: string | null;
}

/** The items behind one problem code: two or more for a clash, one otherwise. */
export interface CodeClash {
  code: string;
  rows: CodeRow[];
}

const same = (a: string | null | undefined, b: string | null | undefined) => !!a && a.trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * Codes and names changed from the panel. A problem's record id is the item code, which isn't
 * unique, so every item with that code is listed: a clash needs all of them, and the read-time
 * check counts them all.
 */
export class CodeFixState {
  field: 'code' | 'name' = 'code';
  clashes: CodeClash[] = [];
  loading = false;
  loadError = false;
  private items: any[] = [];
  // Up on every load and reset, so a reply for an earlier one leaves the panel alone.
  private epoch = 0;

  /** `saved(current)`: false when the panel has moved on since the save started. */
  constructor(private sage: SageIntegrationService, private saved: (current: boolean) => void) {}

  load(group: ErrorGroup, field: 'code' | 'name'): void {
    const at = ++this.epoch;
    this.field = field;
    this.clashes = [];
    this.loading = true;
    this.loadError = false;
    this.sage.getAivorRecords('Item').subscribe({
      next: (items) => {
        if (at !== this.epoch) return;
        this.loading = false;
        this.items = items ?? [];
        // Built once: after a rename the old code finds nothing, and the renamed row should stay shown.
        const codes = [...new Set(group.records.map((r) => (r.recordId ?? '').trim()).filter((c) => c))];
        this.clashes = codes
          .map((code) => ({ code, rows: this.items.filter((i) => same(i.itemCode, code)).map((i) => this.row(i)) }))
          .filter((c) => c.rows.length);
      },
      error: () => {
        if (at !== this.epoch) return;
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  reset(): void {
    this.epoch++;
    this.clashes = [];
    this.items = [];
    this.loading = this.loadError = false;
  }

  private row(item: any): CodeRow {
    const bundle = finishedGoodSyncTarget(item) === 'Bundle';
    const code = item.itemCode ?? '';
    const name = item.itemName ?? '';
    return {
      itemId: item.itemID,
      name,
      code,
      draft: this.field === 'code' ? code : name,
      limit: this.field === 'code' ? (bundle ? BUNDLE_CODE_MAX : CODE_MAX) : bundle ? BUNDLE_NAME_MAX : NAME_MAX,
      saving: false,
      saved: false,
      error: null,
    };
  }

  /** What's wrong with a draft, checked as the user types. */
  problem(row: CodeRow, draft: string): string | null {
    const value = draft.trim();
    if (!value) return this.field === 'code' ? 'Enter an item code.' : 'Enter a name.';
    if (value.length > row.limit) return `${value.length} characters. ${this.field === 'code' ? 'Codes' : 'Names'} can be at most ${row.limit} here.`;
    if (this.field === 'code') {
      const other = this.items.find((i) => i.itemID !== row.itemId && same(i.itemCode, value));
      if (other) return `${other.itemName} already uses ${other.itemCode?.trim()}.`;
    }
    return null;
  }

  /** A row still clashing with another item's code. */
  clashing(row: CodeRow): boolean {
    return this.field === 'code' && this.items.some((i) => i.itemID !== row.itemId && same(i.itemCode, row.code));
  }

  /** The next code no item uses, by counting up the code's last number, or adding one. */
  freeCode(row: CodeRow): string | null {
    const taken = new Set(this.items.map((i) => (i.itemCode ?? '').trim().toLowerCase()));
    const m = row.code.match(/^(.*?)(\d+)$/);
    for (let i = 1; i < 100; i++) {
      const candidate = m ? m[1] + String(Number(m[2]) + i).padStart(m[2].length, '0') : `${row.code.trim()}-${i + 1}`;
      if (candidate.length <= row.limit && !taken.has(candidate.toLowerCase())) return candidate;
    }
    return null;
  }

  save(row: CodeRow): void {
    const value = row.draft.trim();
    if (row.saving || this.problem(row, row.draft) || value === (this.field === 'code' ? row.code : row.name)) return;
    const code = this.field === 'code' ? value : row.code;
    const name = this.field === 'name' ? value : row.name;
    this.update(row, { saving: true, error: null });
    const at = this.epoch;
    this.sage.updateItemCodeAndName(row.itemId, code, name).subscribe({
      next: () => {
        const current = at === this.epoch;
        if (current) {
          const item = this.items.find((i) => i.itemID === row.itemId);
          if (item) Object.assign(item, { itemCode: code, itemName: name });
          this.update(row, { saving: false, saved: true, code, name, draft: value });
        }
        this.saved(current);
      },
      error: (err) => {
        if (at !== this.epoch) return;
        this.update(row, {
          saving: false,
          error: ((err?.status === 400 || err?.status === 409) && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't save it. Try again.",
        });
      },
    });
  }

  setDraft(row: CodeRow, draft: string): void {
    row.draft = draft;
  }

  private update(row: CodeRow, change: Partial<CodeRow>): void {
    this.clashes = this.clashes.map((c) => ({ ...c, rows: c.rows.map((r) => (r.itemId === row.itemId ? Object.assign(r, change) : r)) }));
  }
}
